import { useEffect, useState } from "react";
import { Download, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { toast } from "react-hot-toast";
import { getGrandReport, printGrandReport } from "@/api/reporting";
import ReportingSummaryTable from "../components/reporting-summary-table";
import { useReportTills } from "../use-report-tills";
import {
  buildReportCsvName,
  buildSummaryRows,
  downloadCsv,
  getTodayInputDate,
  parseTillId,
} from "../reporting-utils";
import hasPermission from "@/hooks/hasPermission";

function GrandReportTab({ isActive }) {
  const [tillFilter, setTillFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [summaryRows, setSummaryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const { tills } = useReportTills();

  useEffect(() => {
    if (!isActive) return;

    let alive = true;

    const fetchReport = async () => {
      setIsLoading(true);
      const tillId = parseTillId(tillFilter);
      const response = await getGrandReport({
        date: selectedDate || undefined,
        till_id: tillId,
      });

      if (!alive) return;

      if (response?.status === "success") {
        const data = response?.data || {};
        setSummaryRows(
          buildSummaryRows({
            items: data.payment_methods,
            total: data.total,
            deductions: data.deductions,
            netTotal: data.net_total,
          })
        );
      } else {
        toast.error(response?.message || "Failed to load grand report.");
        setSummaryRows([]);
      }

      setIsLoading(false);
    };

    fetchReport();

    return () => {
      alive = false;
    };
  }, [selectedDate, tillFilter, isActive]);

  const handleExportCsv = () => {
    if (!summaryRows.length) {
      toast.error("No data available to export.");
      return;
    }

    const tillId = parseTillId(tillFilter);
    const fileName = buildReportCsvName("grand-report", selectedDate, tillId);
    const rows = summaryRows.map((row) => [row.label, row.count, row.value]);
    downloadCsv(fileName, [
      "Payment Methods",
      "Number of Order/ Transactions",
      "Total Value",
    ], rows);
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    const tillId = parseTillId(tillFilter);
    const response = await printGrandReport({
      date: selectedDate || undefined,
      till_id: tillId,
    });

    if (response?.status === "success") {
      toast.success(response?.message || "Grand report print dispatched successfully.");
    } else {
      toast.error(response?.message || "Failed to print grand report.");
    }

    setIsPrinting(false);
  };

  return (
    <TabsContent value="grand-report" className="mt-6">
      <div className="pb-12">
        <div className="text-sm font-semibold text-cwhite mb-4">Search By Date</div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-max-content">
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="bg-cblack3 text-sm"
            />
          </div>
          <Select value={tillFilter} onValueChange={setTillFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Filter by Till" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Till</SelectItem>
              {tills.length === 0 ? (
                <SelectItem value="no-tills" disabled>
                  No tills available
                </SelectItem>
              ) : (
                tills.map((till) => (
                  <SelectItem key={till.id} value={String(till.id)}>
                    {till.device_name || `Till-${till.id}`}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {hasPermission("reporting.reporting.print_grand_print") && (
            <>
              <button
                type="button"
                onClick={handleExportCsv}
                className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
              >
                Export as CSV
                <Download size={16} />
              </button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={isPrinting || isLoading}
                className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
              >
                {isPrinting ? "Printing..." : "Print"}
                <Printer size={16} />
              </button>
            </>
          )}
        </div>

        <div>
          {isLoading ? (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              Loading report data...
            </div>
          ) : summaryRows.length > 0 ? (
            <ReportingSummaryTable rows={summaryRows} />
          ) : (
            <div className="rounded-md border border-cblack6 bg-cblack4 px-4 py-3 text-sm text-cwhite3">
              No data found for the selected filters.
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
}

export default GrandReportTab;
